import { sharedEngine } from '../engine/StockfishEngine';
import { clearBackground, isForegroundActive, onIdle, registerBackground } from '../engine/engineGate';
import { getGame, getReport, saveExercises, saveReport } from '../storage/db';
import { buildExercisesFromReport } from '../exercises/buildExercises';
import { analyzeGame } from './analyzeGame';
import type { Color } from '../../types';

export interface AnalysisQueueState {
  pending: string[]; // ids en attente (hors celui en cours)
  current: string | null;
  progress: { phase: 'screening' | 'confirming'; current: number; total: number } | null;
  failed: string[];
}

type StateListener = (state: AnalysisQueueState) => void;
type CompletedListener = (gameId: string) => void;

const RETRY_DELAY_MS = 1500;

/**
 * File d'analyse séquentielle en arrière-plan (une seule partie à la fois,
 * cohérent avec l'instance moteur partagée). Cède systématiquement le pas au
 * premier plan (cf. engineGate) : si une action de l'utilisateur interrompt
 * l'analyse en cours, la partie repasse en tête de file et on retente une
 * fois le calme revenu, plutôt que de perdre la demande.
 */
class AnalysisQueue {
  private queue: string[] = [];
  private failed = new Set<string>();
  private current: string | null = null;
  private progress: AnalysisQueueState['progress'] = null;
  private running = false;
  private stateListeners = new Set<StateListener>();
  private completedListeners = new Set<CompletedListener>();

  getState(): AnalysisQueueState {
    return { pending: [...this.queue], current: this.current, progress: this.progress, failed: [...this.failed] };
  }

  onStateChange(cb: StateListener): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  /** Appelé après chaque partie traitée, avec succès ou en échec définitif
   * (pas sur une simple interruption/reprise) — sert à rafraîchir la
   * bibliothèque affichée sans dépendre de la fréquence des mises à jour de progression. */
  onGameCompleted(cb: CompletedListener): () => void {
    this.completedListeners.add(cb);
    return () => this.completedListeners.delete(cb);
  }

  private emitState() {
    const state = this.getState();
    for (const l of this.stateListeners) l(state);
  }

  private emitCompleted(gameId: string) {
    for (const l of this.completedListeners) l(gameId);
  }

  enqueue(gameIds: string[]): void {
    let changed = false;
    for (const id of gameIds) {
      if (id === this.current || this.queue.includes(id)) continue;
      this.queue.push(id);
      changed = true;
    }
    if (changed) this.emitState();
    void this.run();
  }

  private async waitForForegroundIdle(): Promise<void> {
    if (!isForegroundActive()) return;
    await new Promise<void>((resolve) => {
      const unsubscribe = onIdle(() => {
        unsubscribe();
        resolve();
      });
    });
  }

  private async run(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (this.queue.length > 0) {
        await this.waitForForegroundIdle();
        if (this.queue.length === 0) break;

        const gameId = this.queue.shift()!;
        this.current = gameId;
        this.progress = null;
        this.emitState();

        const controller = new AbortController();
        registerBackground(controller);
        try {
          await this.processGame(gameId, controller.signal);
          this.failed.delete(gameId);
          this.emitCompleted(gameId);
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            // Interrompu par le premier plan : on repasse en tête de file et
            // on laisse un court délai avant de retenter (évite de boucler
            // serré si le premier plan enchaîne plusieurs actions rapides).
            this.queue.unshift(gameId);
            clearBackground();
            this.current = null;
            this.progress = null;
            this.emitState();
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
            continue;
          }
          this.failed.add(gameId);
          this.emitCompleted(gameId);
        }
        clearBackground();
        this.current = null;
        this.progress = null;
        this.emitState();
      }
    } finally {
      this.running = false;
    }
  }

  private async processGame(gameId: string, signal: AbortSignal): Promise<void> {
    const stored = await getGame(gameId);
    if (!stored) return; // partie disparue entre-temps (ex: supprimée) : rien à faire
    const existing = await getReport(gameId);
    if (existing) return; // déjà analysée entre-temps : rien à refaire

    const color: Color = stored.playerColor ?? 'w';
    await sharedEngine.init();
    const report = await analyzeGame(stored.game, color, sharedEngine, {
      signal,
      onProgress: (p) => {
        this.progress = p;
        this.emitState();
      },
    });
    await saveReport(report);
    const exercises = buildExercisesFromReport(stored.game, report);
    if (exercises.length > 0) await saveExercises(exercises);
  }
}

/** Instance partagée : une seule file, cohérente avec le moteur partagé. */
export const analysisQueue = new AnalysisQueue();
